/**
 * What a report says, in sentences — and the report as text to paste.
 *
 * The "analysis" here is a stand-in for an AI reading the report, which is planned for a paid
 * version. It is rules over the numbers already on the screen, labelled as a simulation where it is
 * shown, and it never says anything about a person: the same §43 line the report itself holds.
 */

import type { ReportResult } from './types'

/** The report as Markdown, for pasting into a message or a document. */
export function reportMarkdown(
  report: ReportResult,
  labels: {
    title: string
    period: string
    scope: string
    unassigned: string
    t: (key: string, params?: Record<string, string | number>) => string
  },
): string {
  const { t } = labels
  const out: string[] = []
  out.push(`# ${labels.title} — ${labels.scope}`)
  out.push(labels.period)
  out.push('')
  out.push(`- ${t('reports.workSessions')}: ${report.workSessions}`)
  out.push(`- ${t('reports.tasksCompleted')}: ${report.tasksCompleted} · ${t('reports.bugsFixed')}: ${report.bugsFixed}`)
  out.push(`- ${t('reports.linesChanged')}: +${report.linesAdded} −${report.linesDeleted} · ${t('reports.filesChanged')}: ${report.filesChanged} · ${t('reports.commits')}: ${report.commits ?? 0}`)
  if (report.promptsSent != null) out.push(`- ${t('reports.promptsSent')}: ${report.promptsSent}`)

  for (const project of report.byProject ?? []) {
    out.push('')
    out.push(`## ${project.key === 'unassigned' ? labels.unassigned : project.label}`)
    out.push(`+${project.linesAdded} −${project.linesDeleted} · ${project.filesChanged} ${t('reports.files')} · ${project.commits} ${t('reports.commitsLower')}${project.repositories.length ? ` · ${project.repositories.join(', ')}` : ''}`)
    const list = (title: string, items: typeof project.bugs) => {
      if (items.length === 0) return
      out.push('')
      out.push(`**${title}**`)
      for (const item of items) out.push(`- ${item.key} — ${item.title}${item.done ? ' ✓' : ''}`)
    }
    list(t('reports.bugs'), project.bugs)
    list(t('reports.specs'), project.specs)
    list(t('reports.otherTasks'), project.tasks)
  }

  return out.join('\n')
}
